/**
 * @overview Banner
 * @stage Proposed
 * @author Liz @lizpleo
 * @designer Casper
 */
import type React from 'react'
import styled, {css} from 'styled-components'

import {Box, Link, Text, __alphaTokens as tokens} from '@pleo-io/telescope'

export type BannerVariant = 'default' | 'error' | 'warning' | 'discover'

export interface BannerProps {
    /**
     * Option to change the look of the Banner
     * @default 'default'
     */
    variant?: BannerVariant
    /**
     * The contents of the Banner
     */
    children: React.ReactNode
}

export const PageBanner = ({variant = 'default', children, ...props}: BannerProps) => {
    return (
        <Banner data-generic-ui="page-banner" $variant={variant} {...props}>
            <Box py={8} px={24}>
                {children}
            </Box>
        </Banner>
    )
}

const Banner = styled.div<{$variant: BannerVariant}>`
    width: 100%;
    box-sizing: border-box;
    * {
        box-sizing: border-box;
    }
    ${({$variant}) => {
        const bannerVariant = bannerVariantMap[$variant]
        return css`
            background-color: ${bannerVariant.backgroundColor};
            border-bottom: 1px solid ${bannerVariant.borderColors.bottom};

            &:before {
                content: '';
                display: block;
                height: 4px;
                width: 100%;
                background: ${bannerVariant.borderColors.primary};
                ${bannerVariant.borderColors.secondary &&
                css`
                    background: repeating-linear-gradient(
                        45deg,
                        ${bannerVariant.borderColors.primary},
                        ${bannerVariant.borderColors.primary} 16px,
                        ${bannerVariant.borderColors.secondary} 16px,
                        ${bannerVariant.borderColors.secondary} 32px
                    );
                `}
            }

            * {
                color: ${bannerVariant.textColor};
            }

            a,
            ${Link}, ${BannerLink} {
                color: ${bannerVariant.textColor};
                text-decoration: underline;

                &:visited {
                    color: ${bannerVariant.textColor};
                }

                &:hover {
                    color: ${bannerVariant.linkHoverColor ?? bannerVariant.textColor};
                }
            }
        `
    }}
`

export const BannerText = styled(Text).attrs({weight: 'medium'})``

const BannerLink = styled(Link)`
    font-weight: ${tokens.fontWeightMedium};
`

PageBanner.Text = BannerText
PageBanner.Link = BannerLink

// eslint-disable-next-line string-to-lingui/missing-lingui-transformation
PageBanner.displayName = 'PageBanner'

const bannerVariantMap: Record<
    BannerVariant,
    {
        backgroundColor: string
        textColor: string
        linkHoverColor: string
        borderColors: {primary: string; secondary?: string; bottom: string}
    }
> = {
    default: {
        backgroundColor: tokens.colorBackgroundPresentationalPink,
        textColor: tokens.colorContentStatic,
        linkHoverColor: tokens.colorContentInteractiveLinkHover,
        borderColors: {
            primary: tokens.pink700,
            bottom: tokens.pink400,
        },
    },
    error: {
        backgroundColor: tokens.colorBackgroundNegativeQuiet,
        textColor: tokens.colorContentNegative,
        linkHoverColor: tokens.colorContentInteractiveHover,
        borderColors: {
            primary: tokens.colorBorderNegative,
            secondary: tokens.red500,
            bottom: tokens.colorBorderNegative,
        },
    },
    warning: {
        backgroundColor: tokens.colorBackgroundWarningQuiet,
        textColor: tokens.colorContentWarning,
        linkHoverColor: tokens.colorContentInteractiveHover,
        borderColors: {
            primary: tokens.colorBorderWarning,
            secondary: tokens.yellow900,
            bottom: tokens.colorBorderWarning,
        },
    },
    discover: {
        backgroundColor: tokens.colorBackgroundDiscoverQuiet,
        textColor: tokens.colorContentDiscover,
        linkHoverColor: tokens.colorContentInteractiveHover,
        borderColors: {
            primary: tokens.colorBorderDiscover,
            secondary: tokens.purple400,
            bottom: tokens.colorBorderDiscover,
        },
    },
}
